import * as React from "react";
import { useState, useEffect } from "react";
import {
  Button,
  Menu,
  MenuItem,
  MenuPopover,
  MenuTrigger,
  Tooltip,
  MenuDivider,
} from "@fluentui/react-components";
import { makeStyles } from "@fluentui/react-components";
import { MenuItemData, DropdownMenuProps } from "../types/CommonTypes";

const useStyles = makeStyles({
  buttonContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    gap: "8px",
    color: "var(--colorNeutralForeground1)",
  },
  fixedButton: {
    width: "250px",
    minWidth: "250px",
  },
  fixedMenuItem: {
    width: "240px",
  },
  menuItemContent: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "var(--colorNeutralForeground1)",
  },
  menuHeader: {
    padding: "8px 12px",
    fontSize: "12px",
    fontWeight: "600",
    color: "var(--colorNeutralForeground1)",
    textTransform: "uppercase",
  },
});

export const DropdownMenu = ({
  menuItems,
  defaultValue,
  onSelect,
}: DropdownMenuProps) => {
  const styles = useStyles();
  const [selectedItem, setSelectedItem] = useState<MenuItemData | undefined>(
    undefined
  );

  useEffect(() => {
    // Find and set the default item based on the defaultValue prop
    if (defaultValue) {
      const defaultItem = menuItems.find((item) => item.key === defaultValue);
      if (defaultItem) {
        setSelectedItem(defaultItem);
      }
    } else {
      // Fallback to the first item if no default value is provided
      setSelectedItem(menuItems[0]);
    }
  }, [defaultValue, menuItems]);

  const handleMenuItemClick = (item: MenuItemData) => {
    setSelectedItem(item);
    if (onSelect) {
      onSelect(item);
    }
  };

  const groupedItems = menuItems.reduce(
    (acc, item) => {
      (acc[item.domain] = acc[item.domain] || []).push(item);
      return acc;
    },
    {} as Record<string, MenuItemData[]>
  );

  // Don't render if no item is selected (e.g., empty menuItems array)
  if (!selectedItem) {
    return null;
  }

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button className={styles.fixedButton}>
          <div className={styles.buttonContent}>
            {selectedItem.icon}
            <span>{selectedItem.text}</span>
          </div>
        </Button>
      </MenuTrigger>

      <MenuPopover>
        {Object.keys(groupedItems).map((parent, index) => (
          <React.Fragment key={parent}>
            {index > 0 && <MenuDivider />}
            <div className={styles.menuHeader}>{parent}</div>
            {groupedItems[parent].map((item) => (
              <MenuItem
                key={item.key}
                onClick={() => handleMenuItemClick(item)}
                className={styles.fixedMenuItem}
              >
                <div className={styles.menuItemContent}>
                  {item.icon}
                  <span>{item.text}</span>
                </div>
              </MenuItem>
            ))}
          </React.Fragment>
        ))}
      </MenuPopover>
    </Menu>
  );
};

export default DropdownMenu;
